import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  Button,
  Text,
  HStack,
  Tag,
  useToast,
  Checkbox,
  CheckboxGroup,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Image,
  useColorModeValue,
  Heading,
  Flex,
  Avatar,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useAuth } from "../../context/AuthProvider";
import { getCountries, getCurrencies } from "../../services/dataService";
import { getUserIPInfo, submitUserProfile, awardFreeTradesAfterVerification, fetchUserProfile } from "../../services/userService";
import { sanitizePhoneNumber } from "../../utils/formatters";
import { doc, updateDoc, firestore } from "../../firebase/firebaseConfig";
import { PeerxLogo } from "../../components/icons/Icons";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { getFlagByCountryName } from "../../utils/useFlags";

import { useTranslation } from "react-i18next";


const MotionButton = motion(Button);

const MAX_CURRENCY_SELECTION = 4;

const PersonalInfoPage = ({ initialEmail, userId }) => {
  const { t, i18n } = useTranslation();

  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // Theme-based colors
  const textColor = useColorModeValue("brand.400", "white");

  const [profileData, setProfileData] = useState({
    fullName: "",
    phone: "",
    phoneExtension: "",
    address: {
      street: "",
      city: "",
      state: "",
      postalCode: "",
    },
    country: "",
    currency: "",
    preferredCurrencies: [],
  });

  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {

    const fetchUserProfileData = async () => {
      if (!user?.uid) return;

      try {
        const userProfileData = await fetchUserProfile(user.uid);

        if (userProfileData) {
          if (userProfileData.isProfileUpdated) {
            navigate("/personal/dashboard"); // Redirect verified users
            return;
          }

          setProfileData((prev) => ({
            ...prev,
            ...userProfileData,
          }));
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    fetchUserProfileData();
    setLoading(false);
  }, [user, navigate]);

  useEffect(() => {
    setCountries(getCountries());
    setCurrencies(getCurrencies());

    const fetchIP = async () => {
      const ipData = await getUserIPInfo();
      setProfileData((prev) => ({ ...prev, ipInfo: ipData }));
    };

    fetchIP();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle Language Change
  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: value,
      },
    }));
  };

  /*   const handleCurrencyChange = (e) => {
      const selected = Array.from(e.target.selectedOptions, (option) => option.value);
  
      if (selected.length > MAX_CURRENCY_SELECTION) {
        toast({
          title: "Currency Limit Exceeded",
          description: `You can only select up to ${MAX_CURRENCY_SELECTION} currencies.`,
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
  
      setProfileData({ ...profileData, preferredCurrencies: selected });
    }; */

  const handleCurrencyChange = (selectedCurrencies) => {
    if (selectedCurrencies.length > MAX_CURRENCY_SELECTION) {
      toast({
        title: t("personalinfopage.currency_limit_exceeded_title"),
        description: t("personalinfopage.currency_limit_exceeded_message", { max: MAX_CURRENCY_SELECTION }),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setProfileData((prev) => ({
      ...prev,
      preferredCurrencies: selectedCurrencies,
    }));
  };

  const handleRemoveCurrency = (currency) => {
    setProfileData((prev) => ({
      ...prev,
      preferredCurrencies: prev.preferredCurrencies.filter((code) => code !== currency),
    }));
  };

  const handlePhoneExtensionChange = (e) => {
    const selectedExtension = e.target.value;

    if (!selectedExtension) {
      setProfileData((prev) => ({ ...prev, phoneExtension: "", country: "", countryCode: "" }));
      return;
    }

    const selectedCountry = countries.find((c) => c.dial_code === selectedExtension);
    console.log("🚀 Selected Country:", selectedCountry);


    setProfileData((prev) => ({
      ...prev,
      phoneExtension: selectedExtension,
      country: selectedCountry?.name || prev.country,
      countryCode: selectedCountry?.code || prev.countryCode,
    }));
  };


  const handleSubmit = async () => {
    const { fullName, phone, phoneExtension, address, country, countryCode, preferredCurrencies } = profileData;

    if (!fullName || !phone || !phoneExtension || !address.street || !address.city || !country || !countryCode || preferredCurrencies.length === 0) {
      toast({
        title: t("personalinfopage.missing_info_title"),
        description: t("personalinfopage.missing_info_message"),
        status: "error",
      });
      return;
    }

    const sanitizedPhoneNumber = sanitizePhoneNumber(phoneExtension + phone);

    try {
      const response = await submitUserProfile(user.uid, { ...profileData, phoneNumber: sanitizedPhoneNumber });
      if (response.success) {

        // Mark profile as updated in the users collection
        await updateDoc(doc(firestore, "users", user.uid), {
          isProfileUpdated: true,
        });

        // Check if all conditions are met to award free trades
        await awardFreeTradesAfterVerification(user.uid);

        toast({ title: t("personalinfopage.profile_saved_title"), description: t("personalinfopage.profile_saved_message"), status: "success" });
        navigate("/auth/verify");
      } else {
        throw new Error(t("personalinfopage.profile_submission_failed"));
      }
    } catch (error) {
      toast({
        title: t("personalinfopage.submission_error_title"),
        description: error.message,
        status: "error",
      });
    }
  };

  const flagUrl = profileData.country ? getFlagByCountryName(profileData.country) : null;

  console.log("🚩 Flag URL:", flagUrl);

  return (
    <Box py={10} bg={useColorModeValue("brand.300", "gray.900")} minH="100vh">
      {/* Language Switcher */}
      <Box position="fixed" top="4" right="4" zIndex="120" p={2}>
        <Select
          value={i18n.language}
          onChange={handleLanguageChange}
          width="120px"
          bg="white"
          color="black"
          size="sm"
        >
          <option value="en">English</option>
          <option value="fr">Français</option>
          <option value="es">Español</option>
        </Select>
      </Box>
      <Container maxW="lg">
        {/* Logo */}
        <Heading mb="20px" size="2xl" fontFamily="Poppins" color="brand.200" fontWeight="bold" letterSpacing="wider" as={RouterLink} to="/">
          <PeerxLogo h="90px" w="160px" />
        </Heading>
        <Box
          bg={useColorModeValue("brand.200", "gray.700")}
          borderRadius="lg"
          shadow="xl"
          mt="20px"
          p={8}
          as={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <VStack spacing={6} align="stretch">
            <Box textAlign="center">
              <Heading size="lg" color="brand.300">
                {t("personalinfopage.form_title")}
              </Heading>
              <Text mt={2} fontSize="sm" color={useColorModeValue("gray.600", "gray.300")}>
                {t("personalinfopage.form_description")}
              </Text>
            </Box>

            <FormControl isRequired>
              <FormLabel color={textColor}>{t("personalinfopage.full_name_label")}</FormLabel>
              <Input
                name="fullName"
                value={profileData.fullName}
                onChange={handleInputChange}
                placeholder={t("personalinfopage.full_name_placeholder")}
                bg={useColorModeValue("gray.50", "gray.600")}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={textColor}>{t("personalinfopage.phone_label")}</FormLabel>
              <HStack spacing={4}>
                <Select
                  name="phoneExtension"
                  value={profileData.phoneExtension}
                  onChange={handlePhoneExtensionChange}
                  width="40%"
                  bg={useColorModeValue("gray.50", "gray.600")}
                >
                  <option value="">{t("personalinfopage.phone_extension_select")}</option>
                  {countries.map(({ name, dial_code, code }) => (
                    <option key={code} value={dial_code}>
                      {`(${dial_code})     ${name}`}
                    </option>
                  ))}
                </Select>
                <Input
                  type='tel'
                  name="phone"
                  value={profileData.phone}
                  onChange={handleInputChange}
                  placeholder={t("personalinfopage.phone_placeholder")}
                  bg={useColorModeValue("gray.50", "gray.600")}
                />
              </HStack>
            </FormControl>

            {/*             {flagUrl && (
              <Flex justify="center" mt={4}>
                <Image src={flagUrl} alt={`${profileData.country} ${t("personalinfopage.flag_alt_text")}`} boxSize="64px" />
                </Flex>
            )} */}

            <FormControl isRequired>
              <FormLabel color={textColor}>{t("personalinfopage.country_label")}</FormLabel>
              <HStack spacing={3}>
                {/* Display flag next to the country name */}
                {flagUrl && (

                  <Image src={flagUrl} alt={`${profileData.country} flag`} boxSize="40px" />

                )}
                <Input
                  name="country"
                  value={profileData.country}
                  isReadOnly
                  bg={useColorModeValue("gray.50", "gray.600")}
                  flex="1"
                />
              </HStack>
            </FormControl>


            <FormControl isRequired>
              <FormLabel color={textColor}>{t("personalinfopage.street_label")}</FormLabel>
              <Input
                name="street"
                value={profileData.address.street}
                onChange={handleAddressChange}
                placeholder={t("personalinfopage.street_placeholder")}
                bg={useColorModeValue("gray.50", "gray.600")}
              />
            </FormControl>

            <HStack spacing={4}>
              <FormControl isRequired>
                <FormLabel color={textColor}>{t("personalinfopage.city_label")}</FormLabel>
                <Input
                  name="city"
                  value={profileData.address.city}
                  onChange={handleAddressChange}
                  placeholder={t("personalinfopage.city_placeholder")}
                  bg={useColorModeValue("gray.50", "gray.600")}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={textColor}>{t("personalinfopage.state_label")}</FormLabel>
                <Input
                  name="state"
                  value={profileData.address.state}
                  onChange={handleAddressChange}
                  placeholder={t("personalinfopage.state_placeholder")}
                  bg={useColorModeValue("gray.50", "gray.600")}
                />
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel color={textColor}>{t("personalinfopage.postal_code_label")}</FormLabel>
              <Input
                name="postalCode"
                value={profileData.address.postalCode}
                onChange={handleAddressChange}
                placeholder={t("personalinfopage.postal_code_placeholder")}
                bg={useColorModeValue("gray.50", "gray.600")}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={textColor}>{t("personalinfopage.currency_label")}</FormLabel>
              <Menu>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  bg={useColorModeValue("gray.50", "gray.600")}
                  textAlign="left"
                  width="full"
                  color="brand.400"
                  _hover={{ bg: useColorModeValue("gray.100", "gray.700") }}
                >
                  {t("personalinfopage.currency_select_button")}
                </MenuButton>
                <MenuList
                  maxHeight="200px"
                  overflowY="auto"
                  width="auto"
                  shadow="lg"
                >
                  <CheckboxGroup
                    value={profileData.preferredCurrencies}
                    onChange={handleCurrencyChange}
                  >
                    {currencies.map(({ code, name }) => (
                      <MenuItem key={code} display="flex" alignItems="center">
                        <Checkbox value={code}>
                          {`${name} (${code})`}
                        </Checkbox>
                      </MenuItem>
                    ))}
                  </CheckboxGroup>
                </MenuList>
              </Menu>
              <HStack mt={2} wrap="wrap">
                {profileData.preferredCurrencies.map((code) => (
                  <Tag
                    key={code}
                    colorScheme="teal"
                    m={1}
                    cursor="pointer"
                    onClick={() => handleRemoveCurrency(code)}
                  >
                    {code}
                  </Tag>
                ))}
              </HStack>
            </FormControl>
          </VStack>
        </Box>

        <Flex justify="center" mt={8}>
          <MotionButton
            w="full"
            colorScheme="teal"
            onClick={handleSubmit}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t("personalinfopage.save_button")}
          </MotionButton>
        </Flex>
      </Container>
    </Box>
  );
};

export default PersonalInfoPage;